import React from "react";
import CpLandingShell from "../../components/CpLandingShell/CpLandingShell";
import CpButton from "../../components/CpButton/CpButton";
import CpSectionHeading from "../../components/CpSectionHeading/CpSectionHeading";
import CpAccordion from "../../components/CpAccordion/CpAccordion";
import CpIcon from "../../components/CpIcon/CpIcon";
import {
  FAQS,
  REVIEWS,
  SOAR_CTA_HREF,
  STEPS,
  TARGETS,
  TIERS,
} from "./soarContent";

const builtForChip: React.CSSProperties = {
  padding: "6px 14px",
  borderRadius: 999,
  background: "var(--white)",
  border: "1px solid var(--border-default)",
  fontSize: 13,
  fontWeight: 600,
};

const chipLabel: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: "var(--grey-500)",
  marginRight: 4,
};

const heroFactIcon: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 12,
  background: "var(--ink-500)",
  display: "grid",
  placeItems: "center",
};

const HERO_FACTS = [
  { icon: "map-pin", label: "2 days on-site" },
  { icon: "file-text", label: "Written plan of action" },
  { icon: "calendar-check", label: "Monthly 1:1 follow-up" },
];

const stepCircleBase: React.CSSProperties = {
  width: 56,
  height: 56,
  borderRadius: "50%",
  display: "grid",
  placeItems: "center",
  fontSize: 22,
  fontWeight: 800,
};

/** SOAR — Strategic Operation Action Review landing page. */
const SoarPage: React.FC = () => {
  return (
    <CpLandingShell title="SOAR">
      <section
        data-screen-label="Hero"
        style={{
          background: "var(--cream-100)",
          padding: "clamp(56px, 7vw, 96px) 0",
        }}
      >
        <div
          style={{
            maxWidth: "var(--container-max)",
            margin: "0 auto",
            padding: "0 24px",
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(100%, 440px), 1fr))",
            gap: 56,
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 14px",
                  borderRadius: 999,
                  background: "var(--red-50)",
                  border: "1px solid rgba(178,0,48,.15)",
                  color: "var(--red-500)",
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  lineHeight: 1.2,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "var(--red-500)",
                    boxShadow: "0 0 0 3px rgba(178,0,48,.18)",
                  }}
                />
                Coaching
              </span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "6px 14px",
                  borderRadius: 999,
                  background: "var(--ink-500)",
                  color: "var(--cream-100)",
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  lineHeight: 1.2,
                }}
              >
                Limited Spots
              </span>
            </div>
            <h1
              style={{
                margin: "22px 0 0",
                fontSize: "clamp(64px, 9vw, 112px)",
                lineHeight: 0.95,
                letterSpacing: "-.04em",
                fontWeight: 800,
                color: "var(--red-500)",
              }}
            >
              SOAR
            </h1>
            <p
              style={{
                margin: "10px 0 0",
                fontSize: "clamp(22px, 2.4vw, 30px)",
                fontWeight: 300,
                letterSpacing: "-.015em",
                lineHeight: 1.2,
                color: "var(--ink-500)",
              }}
            >
              Strategic Operation Action Review
            </p>
            <p
              style={{
                margin: "22px 0 0",
                maxWidth: "34ch",
                fontSize: "clamp(18px, 1.6vw, 21px)",
                lineHeight: 1.5,
                color: "var(--ink-500)",
              }}
            >
              A two-day, on-site strategic review of your business combined with
              monthly coaching designed to deliver exceptional results.
            </p>

            <div
              style={{
                marginTop: 26,
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <span style={chipLabel}>Built for</span>
              <span style={builtForChip}>Owners</span>
              <span style={builtForChip}>Leadership Teams</span>
              <span style={builtForChip}>All Trades</span>
            </div>

            <div
              style={{
                marginTop: 12,
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <span style={chipLabel}>Available on</span>
              {TIERS.map((t) => (
                <span
                  key={t}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 14px 6px 11px",
                    borderRadius: 999,
                    background: "var(--white)",
                    border: "1px solid var(--border-strong)",
                    fontSize: 13,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  <CpIcon color="var(--red-500)" name="plus" size={13} />
                  {t}
                  <span
                    style={{
                      marginLeft: 2,
                      paddingLeft: 8,
                      borderLeft: "1px solid var(--border-default)",
                      fontSize: 10,
                      fontWeight: 800,
                      letterSpacing: ".1em",
                      textTransform: "uppercase",
                      color: "var(--grey-500)",
                    }}
                  >
                    Add-on
                  </span>
                </span>
              ))}
            </div>

            <div
              style={{
                marginTop: 34,
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <CpButton href={SOAR_CTA_HREF} size="lg">
                Explore SOAR
              </CpButton>
              <CpButton href="#how-it-works" size="lg" variant="secondary">
                See How It Works
              </CpButton>
            </div>

            <div
              style={{
                marginTop: 40,
                paddingTop: 24,
                borderTop: "1px solid var(--border-default)",
                display: "flex",
                gap: 28,
                flexWrap: "wrap",
              }}
            >
              {HERO_FACTS.map((f) => (
                <div
                  key={f.label}
                  style={{ display: "flex", alignItems: "center", gap: 12 }}
                >
                  <div style={heroFactIcon}>
                    <CpIcon color="var(--gold-400)" name={f.icon} size={20} />
                  </div>
                  <span style={{ fontSize: 15, fontWeight: 700 }}>
                    {f.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div
            aria-label="What SOAR targets"
            style={{
              background: "var(--ink-500)",
              borderRadius: 24,
              boxShadow: "var(--shadow-lg)",
              overflow: "hidden",
              color: "var(--cream-100)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 24px",
                borderBottom: "1px solid var(--border-inverse)",
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 700 }}>
                What SOAR is built to find
              </span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  color: "var(--gold-400)",
                }}
              >
                Quarterly
              </span>
            </div>
            <div style={{ padding: "8px 24px" }}>
              {TARGETS.map((g) => (
                <div
                  key={g.l}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "132px minmax(0, 1fr)",
                    gap: 20,
                    alignItems: "center",
                    padding: "20px 0",
                    borderBottom: "1px solid var(--border-inverse)",
                  }}
                >
                  <span
                    style={{
                      fontSize: 36,
                      fontWeight: 800,
                      letterSpacing: "-.03em",
                      lineHeight: 1,
                      color: "var(--cream-100)",
                    }}
                  >
                    {g.v}
                  </span>
                  <span
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      lineHeight: 1.4,
                      color: "var(--ink-100)",
                    }}
                  >
                    {g.l}
                  </span>
                </div>
              ))}
            </div>
            <div
              style={{
                padding: "20px 24px 24px",
                background: "var(--ink-900)",
                display: "flex",
                alignItems: "center",
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "var(--gold-400)",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <CpIcon color="var(--ink-500)" name="user-check" size={20} />
              </div>
              <span
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: "var(--cream-100)",
                }}
              >
                Led by a CertainPath Strategic Business Advisor, in your
                building
              </span>
            </div>
          </div>
        </div>
      </section>

      <section
        data-screen-label="What Is SOAR"
        style={{
          background: "var(--ink-500)",
          padding: "clamp(56px, 7vw, 96px) 0",
        }}
      >
        <div
          style={{
            maxWidth: 1160,
            margin: "0 auto",
            padding: "0 24px",
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
            gap: "24px 64px",
            alignItems: "start",
          }}
        >
          <h2
            style={{
              margin: 0,
              color: "var(--gold-400)",
              fontSize: "clamp(30px, 3.2vw, 42px)",
              fontWeight: 800,
              letterSpacing: "-.025em",
              lineHeight: 1.12,
            }}
          >
            What is SOAR?
          </h2>
          <div>
            <p
              style={{
                margin: 0,
                color: "var(--cream-100)",
                fontSize: "clamp(20px, 1.9vw, 24px)",
                fontWeight: 300,
                lineHeight: 1.45,
              }}
            >
              Drive revenue another 20%+. Add 5–10% to your bottom line. Or
              both.{" "}
              <strong style={{ fontWeight: 800 }}>
                The fastest way there is an expert, outside look at your
                company.
              </strong>
            </p>
            <p
              style={{
                margin: "18px 0 0",
                color: "var(--cream-100)",
                fontSize: 17,
                lineHeight: 1.65,
              }}
            >
              One of CertainPath&apos;s Strategic Business Advisors comes to
              your company on-site. By stepping into your operation, talking to
              your people, observing workflows and studying your financials,
              they give you fresh insight into what&apos;s limiting your
              company&apos;s true potential — and a quarterly course of action
              to develop a plan and follow through.
            </p>
          </div>
        </div>
      </section>

      <section
        data-screen-label="The Two-Day Review"
        id="review"
        style={{ background: "var(--sand-200)", padding: "var(--section-y) 0" }}
      >
        <div
          style={{
            maxWidth: "var(--container-max)",
            margin: "0 auto",
            padding: "0 24px",
          }}
        >
          <CpSectionHeading
            align="center"
            eyebrow="The Two-Day Review"
            lead="Your advisor works side by side with you for two days. Here's what they examine."
            title="Review 10 Areas of Your Operation"
          />
          <div
            data-stagger=""
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
              gap: 16,
              marginTop: 48,
            }}
          >
            {REVIEWS.map((r) => (
              <div
                key={r.title}
                style={{
                  background: "var(--white)",
                  border: "1px solid var(--border-default)",
                  borderRadius: 16,
                  padding: 24,
                  display: "grid",
                  gridTemplateColumns: "48px minmax(0, 1fr)",
                  gap: 18,
                  alignItems: "start",
                  boxShadow: "var(--shadow-sm)",
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 14,
                    background: "var(--ink-500)",
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <CpIcon color="var(--gold-400)" name={r.icon} size={22} />
                </div>
                <div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: 18,
                      lineHeight: 1.3,
                      letterSpacing: "-.01em",
                    }}
                  >
                    {r.title}
                  </h3>
                  <p
                    style={{
                      margin: "6px 0 0",
                      fontSize: 15,
                      lineHeight: 1.55,
                      color: "var(--text-muted)",
                      textWrap: "pretty",
                    }}
                  >
                    {r.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        data-screen-label="How It Works"
        id="how-it-works"
        style={{ background: "var(--ink-500)", padding: "var(--section-y) 0" }}
      >
        <div
          style={{
            maxWidth: "var(--container-max)",
            margin: "0 auto",
            padding: "0 24px",
          }}
        >
          <CpSectionHeading
            align="center"
            eyebrow="How It Works"
            title="Hit Your Targets within One Quarter"
            tone="dark"
          />
          <ol
            data-stagger=""
            style={{
              listStyle: "none",
              margin: "48px 0 0",
              padding: 0,
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(min(100%, 210px), 1fr))",
              gap: 20,
            }}
          >
            {STEPS.map((s, i) => {
              const last = i === STEPS.length - 1;
              return (
                <li
                  key={s.title}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 18,
                    textAlign: "center",
                  }}
                >
                  <div
                    style={
                      last
                        ? {
                            ...stepCircleBase,
                            background: "var(--gold-400)",
                            color: "var(--ink-500)",
                          }
                        : {
                            ...stepCircleBase,
                            border: "2px solid var(--gold-400)",
                            color: "var(--gold-400)",
                            boxSizing: "border-box",
                          }
                    }
                  >
                    {i + 1}
                  </div>
                  <div
                    style={{
                      background: "var(--white)",
                      borderRadius: 16,
                      padding: "26px 22px",
                      width: "100%",
                      boxSizing: "border-box",
                      flex: 1,
                    }}
                  >
                    <h3 style={{ margin: 0, fontSize: 18, lineHeight: 1.3 }}>
                      {s.title}
                    </h3>
                    <p
                      style={{
                        margin: "10px 0 0",
                        fontSize: 15,
                        lineHeight: 1.55,
                        color: "var(--text-muted)",
                      }}
                    >
                      {s.body}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
          <div
            style={{
              marginTop: 48,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CpButton href={SOAR_CTA_HREF} size="lg">
              Explore SOAR
            </CpButton>
          </div>
        </div>
      </section>

      <section
        data-screen-label="Limited Spots"
        style={{
          background: "var(--cream-100)",
          padding: "var(--section-y) 0",
        }}
      >
        <div style={{ maxWidth: 1080, margin: "0 auto", padding: "0 24px" }}>
          <div
            style={{
              background: "var(--white)",
              border: "1px solid var(--border-default)",
              borderRadius: 24,
              boxShadow: "var(--shadow-md)",
              padding: "clamp(32px, 5vw, 56px)",
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
              gap: "32px 56px",
              alignItems: "center",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  color: "var(--red-500)",
                }}
              >
                Limited Spots Available
              </div>
              <h2
                style={{
                  margin: "12px 0 0",
                  fontSize: "clamp(30px, 3.4vw, 44px)",
                  fontWeight: 800,
                  letterSpacing: "-.025em",
                  lineHeight: 1.1,
                  textWrap: "balance",
                }}
              >
                Only a Few Spots Are Left
              </h2>
            </div>
            <div>
              <p
                style={{
                  margin: 0,
                  fontSize: 17,
                  lineHeight: 1.65,
                  color: "var(--ink-500)",
                }}
              >
                Because of the one-on-one engagement and the time it takes to
                come to your location for a two-day review, only a small number
                of CertainPath members can be enrolled. If all spots are filled,
                we&apos;ll let you know you&apos;re on the waitlist.
              </p>
              <div style={{ marginTop: 24 }}>
                <CpButton href={SOAR_CTA_HREF} size="lg">
                  Get Registered Here
                </CpButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        data-screen-label="FAQ"
        id="faq"
        style={{
          background: "var(--sand-200)",
          padding: "var(--section-y) 0 48px",
        }}
      >
        <div style={{ maxWidth: 920, margin: "0 auto", padding: "0 24px" }}>
          <CpSectionHeading
            align="center"
            eyebrow="Questions"
            title="Frequently Asked Questions"
          />
          <div style={{ marginTop: 40 }}>
            <CpAccordion items={FAQS} />
          </div>
        </div>
      </section>

      <section
        data-screen-label="CTA"
        style={{
          background: "var(--sand-200)",
          padding: "48px 0 var(--section-y)",
        }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
          <div
            style={{
              background: "var(--gradient-brand)",
              borderRadius: 24,
              padding: "clamp(56px, 7vw, 88px) clamp(24px, 5vw, 56px)",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 18,
              boxShadow: "var(--shadow-lg)",
            }}
          >
            <h2
              style={{
                margin: 0,
                color: "var(--cream-100)",
                fontSize: "var(--text-display-2)",
                lineHeight: 1.08,
                letterSpacing: "-.025em",
                maxWidth: "20ch",
                textWrap: "balance",
              }}
            >
              Ready to Take Your Company Higher?
            </h2>
            <p
              style={{
                margin: "0 0 14px",
                color: "var(--cream-100)",
                fontSize: 18,
                lineHeight: 1.55,
                maxWidth: "54ch",
              }}
            >
              Fill out the form to tell us what you&apos;re hoping to achieve.
              Our team will be in touch to discuss the program.
            </p>
            <CpButton href={SOAR_CTA_HREF} size="lg" variant="inverse">
              Get Registered Here
            </CpButton>
          </div>
        </div>
      </section>
    </CpLandingShell>
  );
};

export default SoarPage;
